"use client";

import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import InputMask from "react-input-mask";
import { Loader2 } from "lucide-react";

import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { useStore } from "../app/stores/store";
import { ContactSchema, contactSchema } from "../lib/schemas/contactSchema";
import { useToast } from "../hooks/use-toast";
import { Input } from "./ui/input";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

type InquiryFormValues = z.infer<typeof contactSchema>;

export function InquiryForm() {
  const { toast } = useToast();
  const {
    contactStore: { addContact },
  } = useStore();

  const form = useForm<InquiryFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  const {
    formState: { isSubmitting },
  } = form;

  const onSubmit = async (values: InquiryFormValues) => {
    try {
      const result = await addContact(values);
      if (result.status === "success") {
        toast({
          title: "Inquiry Sent!",
          description:
            "We've received your inquiry and will get back to you shortly.",
        });
        form.reset();
      } else {
        toast({
          title: "Error",
          description: "Something went wrong",
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Something went wrong",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="border-gray-200 bg-white rounded-xl w-full">
      <CardHeader>
        <CardTitle>Have a Question?</CardTitle>
      </CardHeader>
      <CardContent className="p-6 sm:p-8">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Your Name"
                      {...field}
                      className="!border-gray-300 hover:border-[3px] focus-visible:ring-[#e71875] focus-visible:border-[#e71875] transition-colors duration-200"
                    />
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />

            {/* Email */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      {...field}
                      className="hover:border-[3px] focus-visible:ring-[#e71875] focus-visible:border-[#e71875] transition-colors duration-200"
                    />
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />

            {/* Phone */}
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <div className="hover:border-[#e71875] transition-colors duration-200 rounded-md border border-input focus-visible:ring-[#e71875] focus-visible:border-[#e71875]">
                      <PhoneInput
                        international
                        defaultCountry="AE"
                        placeholder="Enter phone number"
                        value={field.value}
                        onChange={field.onChange}
                        className="[&>input]:border-0 [&>input]:ring-0 [&>input]:outline-none [&>input]:focus:ring-0 [&>input]:focus:outline-none flex items-center w-full bg-background px-3 py-2 text-base"
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />

            {/* Message */}
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <textarea
                      rows={4}
                      placeholder="Type your message..."
                      {...field}
                      className="w-full  rounded-md bg-background px-3 py-2 text-base placeholder:text-muted-foreground  focus-visible:ring-[#e71875] focus-visible:border-[#e71875] transition-colors duration-200"
                    />
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full text-lg py-4 mt-4 hover:bg-[#e71875] transition-colors duration-200"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2 inline-block" />
              ) : (
                "Submit Now"
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
